import { addDays } from "date-fns/addDays";
import { startOfToday } from "date-fns/startOfToday";
import {
  type Identifier,
  type SortPayload,
  useGetIdentity,
  useLocaleState,
  useTimeout,
  useTranslate,
} from "ra-core";
import { useIsMobile } from "@/hooks/use-mobile";

import { TaskListFilter } from "./TasksListFilter";
import { TasksListByOwner } from "./TasksListByOwner";
import {
  DEFAULT_TASK_DUE_BUCKETS,
  resolveNullFilterValues,
  type TaskDueBucket,
  type TaskFilter,
} from "./taskDueBuckets";
import type { TasksScope } from "./tasksScope";
import { isBeforeFriday } from "./tasksPredicate";
import { useTaskDueBucket, type TaskDueBucketResult } from "./useTaskDueBucket";

const DEFAULT_SORT: SortPayload = { field: "due_date", order: "ASC" };

/** Buckets that say so when they are empty, rather than disappearing. */
const BUCKET_EMPTY_MESSAGES: Partial<Record<TaskDueBucket, string>> = {
  overdue: "resources.tasks.empty_overdue",
  today: "resources.tasks.empty_today",
};

export const TasksListByDueDate = ({
  filterByContact,
  emptyPlaceholder,
  pendingPlaceholder,
  scope = "mine",
  groupByOwner = false,
  buckets = DEFAULT_TASK_DUE_BUCKETS,
  filter,
  sort = DEFAULT_SORT,
}: {
  filterByContact?: Identifier;
  emptyPlaceholder?: React.ReactNode;
  pendingPlaceholder?: React.ReactNode;
  /** "all" drops the per-account-manager filter. Admin surfaces only. */
  scope?: TasksScope;
  /** Sub-group each due-date bucket by owning account manager. */
  groupByOwner?: boolean;
  /** Which buckets to render, in order. Defaults to the five open ones. */
  buckets?: TaskDueBucket[];
  /** Extra provider filter (the list page's own filters) merged into each query. */
  filter?: TaskFilter;
  sort?: SortPayload;
}) => {
  const { identity } = useGetIdentity();
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const [locale] = useLocaleState();

  const isAllScope = filterByContact == null && scope === "all";

  const scopeFilter: TaskFilter =
    filterByContact != null
      ? { contact_id: filterByContact }
      : isAllScope
        ? {}
        : { sales_id: identity?.id };
  // The rail's null tests reach us as the string a query string can carry; the
  // buckets query the provider, so they need the real thing.
  const listFilter: TaskFilter = resolveNullFilterValues({
    ...scopeFilter,
    ...filter,
  });
  const isReady =
    filterByContact != null || isAllScope ? true : identity != null;

  // Friday onwards, "this week" is behind "tomorrow" and holds nothing a
  // contact aside is any better for showing.
  const visibleBuckets = buckets.filter(
    (bucket) =>
      bucket !== "this_week" || filterByContact == null || isBeforeFriday(),
  );
  const isVisible = (bucket: TaskDueBucket) =>
    isReady && visibleBuckets.includes(bucket);

  // One query per bucket, each a narrow range on due_date. Fixed call order:
  // the set of buckets is a constant, only their `enabled` flag varies.
  const results: Record<TaskDueBucket, TaskDueBucketResult> = {
    overdue: useTaskDueBucket(
      "overdue",
      listFilter,
      sort,
      isVisible("overdue"),
    ),
    today: useTaskDueBucket("today", listFilter, sort, isVisible("today")),
    tomorrow: useTaskDueBucket(
      "tomorrow",
      listFilter,
      sort,
      isVisible("tomorrow"),
    ),
    this_week: useTaskDueBucket(
      "this_week",
      listFilter,
      sort,
      isVisible("this_week"),
    ),
    later: useTaskDueBucket("later", listFilter, sort, isVisible("later")),
    no_due_date: useTaskDueBucket(
      "no_due_date",
      listFilter,
      sort,
      isVisible("no_due_date"),
    ),
    completed: useTaskDueBucket(
      "completed",
      listFilter,
      sort,
      isVisible("completed"),
    ),
  };

  const oneSecondHasPassed = useTimeout(1000);
  const isPending =
    !isReady || visibleBuckets.some((bucket) => results[bucket].isPending);
  const totalTasks = visibleBuckets.reduce(
    (sum, bucket) => sum + results[bucket].total,
    0,
  );

  if (isPending) {
    return oneSecondHasPassed ? (pendingPlaceholder ?? null) : null;
  }

  if (totalTasks === 0) {
    return emptyPlaceholder ?? null;
  }

  // Today and Tomorrow each cover exactly one day, so the heading can name it.
  // Overdue / This week / Later span a range and stay label-only.
  const withDate = (label: string, date: Date) =>
    translate("resources.tasks.filters.with_date", {
      label,
      date: date.toLocaleDateString(locale, {
        month: "short",
        day: "numeric",
      }),
    });

  const getTitle = (bucket: TaskDueBucket) => {
    switch (bucket) {
      case "today":
        return withDate(
          translate("resources.tasks.filters.today"),
          startOfToday(),
        );
      case "tomorrow":
        return withDate(
          translate("resources.tasks.filters.tomorrow"),
          addDays(startOfToday(), 1),
        );
      case "completed":
        return translate("resources.tasks.filter.done");
      default:
        return translate(`resources.tasks.filters.${bucket}`);
    }
  };

  const showContact = filterByContact == null;
  const showOwner = isAllScope && !groupByOwner;

  // A due-date bucket renders either as one flat list or, when grouping by
  // owner, as one sub-list per account manager. The date buckets themselves are
  // unchanged either way.
  const renderBucket = (bucket: TaskDueBucket) => {
    const { tasks, total, hasMore, loadMore } = results[bucket];
    const title = getTitle(bucket);
    const onLoadMore = hasMore ? loadMore : undefined;

    return groupByOwner ? (
      <TasksListByOwner
        key={bucket}
        tasks={tasks}
        title={title}
        total={total}
        showContact={showContact}
        includeDone={bucket === "completed"}
        onLoadMore={onLoadMore}
        isMobile={isMobile}
      />
    ) : (
      <TaskListFilter
        key={bucket}
        tasks={tasks}
        title={title}
        total={total}
        showContact={showContact}
        showOwner={showOwner}
        includeDone={bucket === "completed"}
        // A contact aside is a compact widget: an empty bucket there is noise,
        // not the missing signal it is on the task list page.
        emptyMessage={
          filterByContact == null ? BUCKET_EMPTY_MESSAGES[bucket] : undefined
        }
        onLoadMore={onLoadMore}
        hideDueDateWhenSingleDay
        isMobile={isMobile}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {visibleBuckets.map(renderBucket)}
    </div>
  );
};
